import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../../common/types';
import { LoadingIcon } from '../../../../common/views/LoadingIcon';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { useShowToast } from '../../../../../redux/slices/toasts/hooks';
import { ToastType } from '../../../../../redux/slices/toasts/state';
import { useMusic24Capabilities } from '../../../../music24/hooks/useMusic24Capabilities';
import { useOpenReleasePage } from '../../../../music24/promote/releases/useOpenReleasePage';
import { addSongFlowMessages } from '../addSongFlowMessages';
import { StepHeader } from '../StepHeader';
import { shouldMakeFirstPresave, useOnboardingFirstSong } from '../useOnboardingFirstSong';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface OnboardingDoneStepProps {
  accountId: number;
  item: CatalogItem;
}

/**
 * Done inside onboarding (design catalog-add-song f4, onboarding c4): the free images of the first
 * release start, then the release page opens on the Assets tab (onboarding c5). A song with a future
 * release day on a plan with pre-saves also gets its published pre-save page here (QA F03, D10), and
 * a toast names its address.
 */
const OnboardingDoneStepComponent: React.FC<OnboardingDoneStepProps> = ({ accountId, item }) => {
  const intl = useIntl();
  const { startFirstImages, startPresavePage } = useOnboardingFirstSong(accountId);
  const { canUsePresave } = useMusic24Capabilities();
  const { show } = useShowToast();
  const openReleasePage = useOpenReleasePage({ accountId });
  const [isStarted, setStarted] = useState(false);
  const [presaveUrl, setPresaveUrl] = useState<string | null>(null);
  const hasRun = useRef(false);
  // Read once, at the first render: the row changes (a pre-save page) while the calls run.
  const [makesPresave] = useState(() => shouldMakeFirstPresave(item, canUsePresave, Date.now()));

  useEffect(() => {
    if (hasRun.current) {
      return;
    }
    hasRun.current = true;
    const presave = startPresavePage(item)
      .then((page) => {
        if (page !== null) {
          setPresaveUrl(page.shareUrl);
          // `linkUrl` draws the address as a link with Open and Copy (QA mob-11).
          show({
            type: ToastType.success,
            description: intl.formatMessage(addSongFlowMessages.doneOnboardingPresaveLiveToast),
            linkUrl: page.shareUrl,
            autoHide: true,
          });
        }
      })
      .catch(console.error);
    Promise.all([startFirstImages(item).catch(console.error), presave])
      .catch(console.error)
      .finally(() => {
        setStarted(true);
        openReleasePage(item.uuid);
      });
  }, [intl, item, openReleasePage, show, startFirstImages, startPresavePage]);

  return (
    <>
      <StepHeader
        question={intl.formatMessage(addSongFlowMessages.doneOnboardingQuestion, { title: item.title })}
        sub={intl.formatMessage(addSongFlowMessages.doneOnboardingSub)}
      />
      <div className={styles.reading}>
        <div className={styles.readingLine}>
          <FontAwesomeIcon icon={faCheck} className={styles.savedIcon} />
          {intl.formatMessage(addSongFlowMessages.doneOnboardingAdded)}
        </div>
        <div className={styles.readingLine}>
          <FontAwesomeIcon icon={faCheck} className={styles.savedIcon} />
          {intl.formatMessage(addSongFlowMessages.doneOnboardingFirst)}
        </div>
        <div className={styles.readingLine}>
          {isStarted ? <FontAwesomeIcon icon={faCheck} className={styles.savedIcon} /> : <LoadingIcon small />}
          {intl.formatMessage(addSongFlowMessages.doneOnboardingImages)}
        </div>
        {/* A pre-save that could not be made shows no line: the release page offers it. */}
        {makesPresave && (!isStarted || presaveUrl !== null) && (
          <div className={styles.readingLine}>
            {presaveUrl === null ? (
              <LoadingIcon small />
            ) : (
              <FontAwesomeIcon icon={faCheck} className={styles.savedIcon} />
            )}
            {presaveUrl === null
              ? intl.formatMessage(addSongFlowMessages.doneOnboardingPresave)
              : intl.formatMessage(addSongFlowMessages.doneOnboardingPresaveLive, {
                  // A value element is a list item of the message: it needs a key (QA impr2-3).
                  url: (
                    <a key="url" href={presaveUrl} target="_blank" rel="noreferrer">
                      {presaveUrl}
                    </a>
                  ),
                })}
          </div>
        )}
      </div>
      <div className={styles.actions}>
        <span className={styles.actionsNote} />
        <SMLButton color="primary" size="lg" omitCap isLoading={!isStarted} onClick={() => openReleasePage(item.uuid)}>
          {intl.formatMessage(addSongFlowMessages.doneOnboardingOpen)}
        </SMLButton>
      </div>
    </>
  );
};

export const OnboardingDoneStep = TypedMemo(OnboardingDoneStepComponent);
